// What a wearable says about itself in its Bluetooth advert, for the
// "Update over Bluetooth" screen.
//
// Docked wearables (on USB, firmware v0.9.0+) advertise as "SIT-XXXX" with
// manufacturer data under company 0xFFFF (wearable-firmware
// docs/engineering-guide/receiver-protocol.md §7), little-endian:
//
//   [0]     layout version 0x01
//   [1]     battery % (0xFF = unknown)
//   [2..3]  records waiting to be synced
//   [4..7]  flash bytes used
//   [8..10] internal firmware version major, minor, patch (0xFF = unknown)
//   [11]    build: 0 local, 1 production, 2 dev, 3 demo
//   [12]    power (v0.10.0+): bit 0 on USB power, bit 1 charge complete
//
// Older firmware sends only the first 8 bytes, and only while records wait.
// The main process (electron/bleFleet.js) passes the raw bytes as hex, company
// ID first, as CoreBluetooth reports them.

import { compareVersions } from "./firmwareRelease";

const COMPANY = 0xffff;
const LAYOUT = 0x01;
export const BUILD_KINDS = ["local", "production", "dev", "demo"];

const hexBytes = (hex) => {
  const s = String(hex || "");
  if (!/^([0-9a-f]{2})*$/i.test(s)) return null;
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
};

/**
 * Manufacturer data (hex, company ID first) → { battery, records, bytesUsed,
 * version, build, power }, or null if it isn't a wearable's payload.
 * `version` and `build` are null before firmware v0.9.0; `power` ("charging",
 * "charged" or "battery") is null before v0.10.0.
 */
export const parseWearableAdvert = (hex) => {
  const b = hexBytes(hex);
  if (!b || b.length < 10) return null;
  if ((b[0] | (b[1] << 8)) !== COMPANY || b[2] !== LAYOUT) return null;
  const p = b.subarray(2);
  const advert = {
    battery: p[1] === 0xff ? null : p[1],
    records: p[2] | (p[3] << 8),
    bytesUsed: (p[4] | (p[5] << 8) | (p[6] << 16) | (p[7] << 24)) >>> 0,
    version: null,
    build: null,
    power: null,
  };
  if (p.length >= 12 && ![p[8], p[9], p[10]].includes(0xff)) {
    advert.version = `v${p[8]}.${p[9]}.${p[10]}`;
    advert.build = BUILD_KINDS[p[11]] || null;
  }
  if (p.length >= 13) {
    const onUsb = (p[12] & 0x01) !== 0;
    const complete = (p[12] & 0x02) !== 0;
    advert.power = !onUsb ? "battery" : complete ? "charged" : "charging";
  }
  return advert;
};

// Clearing all records over Bluetooth needs firmware v0.14.0, on USB.
export const CLEAR_FIRMWARE = "v0.14.0";

/** True when a unit's advert says it can be cleared from here. */
export const canClear = (advert) =>
  !!advert?.version &&
  ["charging", "charged"].includes(advert.power) &&
  (compareVersions(advert.version, CLEAR_FIRMWARE) ?? -1) >= 0;

/** Production releases → Map of internal version → product version. */
export const productVersionsFrom = (releases) =>
  new Map(
    (releases || [])
      .filter((r) => r?.base_version && r?.product_version)
      .map((r) => [r.base_version, r.product_version])
  );

/** The version to show for an internal version: its product version if any. */
export const versionLabel = (version, productVersions) =>
  (version && productVersions?.get(version)) || version;

/**
 * Devices ([{ id, advert }]) grouped by firmware version, newest first, with
 * the ones that don't report a version last:
 * [{ version (null = unknown), ids }].
 */
export const groupByVersion = (devices) => {
  const groups = new Map();
  for (const d of devices || []) {
    const version = d.advert?.version || null;
    if (!groups.has(version)) groups.set(version, { version, ids: [] });
    groups.get(version).ids.push(d.id);
  }
  return [...groups.values()].sort((a, b) => {
    if (!a.version) return 1;
    if (!b.version) return -1;
    return compareVersions(b.version, a.version) || 0;
  });
};
